import { inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, Data, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TITLE_CONFIG } from './provider';

@Injectable({ providedIn: 'root' })
export class CustomTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);
  private readonly titleConfig = inject(TITLE_CONFIG, { optional: true });

  /**
   * Sets the title of the browser to the given value.
   */
  override updateTitle(snapshot: RouterStateSnapshot): void {
    let resolvedTitle = this.buildTitle(snapshot);
    const routeData = this.getDeepestRouteData(snapshot.root);

    if (resolvedTitle === undefined) {
      resolvedTitle = routeData?.['title'] as string | undefined;
      const breadcrumbLabel = (routeData?.['breadcrumb'] as { label?: string } | undefined)?.label;
      if (!resolvedTitle && breadcrumbLabel && !breadcrumbLabel.startsWith(':')) {
        resolvedTitle = breadcrumbLabel;
      }
    }

    const fullTitle = this.formatTitle(resolvedTitle, routeData);

    if (fullTitle && this.title.getTitle() !== fullTitle) {
      this.title.setTitle(fullTitle);
    }
  }

  formatTitle(pageTitle?: string, routeData?: Data): string {
    if (!pageTitle) {
      return this.titleConfig?.trailing ?? this.titleConfig?.leading ?? '';
    }

    const leading = this.titleConfig?.leading;
    const trailing = this.titleConfig?.trailing;
    const titleSeparator = this.titleConfig?.separator ?? '|';

    const isLeading = routeData?.['titleLeading'] === true || routeData?.['titlePosition'] === 'leading';
    if (isLeading && trailing) {
      if (pageTitle.startsWith(trailing)) {
        return pageTitle.trim();
      }
      return `${trailing} ${titleSeparator} ${pageTitle}`.trim();
    }

    if (trailing && (pageTitle.startsWith(trailing) || pageTitle.endsWith(trailing))) {
      return pageTitle.trim();
    }

    const parts = [leading, pageTitle, trailing].filter(Boolean);
    return parts.join(` ${titleSeparator} `).trim();
  }

  setDynamicTitle(pageTitle: string, options?: { leading?: boolean }): void {
    const fullTitle = this.formatTitle(pageTitle, options?.leading ? { titleLeading: true } : undefined);
    if (fullTitle && this.title.getTitle() !== fullTitle) {
      this.title.setTitle(fullTitle);
    }
  }

  private getDeepestRouteData(route: ActivatedRouteSnapshot): Data | undefined {
    let current: ActivatedRouteSnapshot | null = route;
    let accumulatedData: Data | undefined;

    while (current) {
      if (current.data && Object.keys(current.data).length > 0) {
        accumulatedData = { ...accumulatedData, ...current.data };
      }
      current = current.firstChild;
    }

    return accumulatedData;
  }
}
